import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "wouter";
import { ChevronRight, ExternalLink, MapPin, Check, Share2 } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { subscribeToNewsletter } from "@/lib/brevo";

// Hero image — placeholder until user provides CDN URL
const HERO_IMG = "https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/SIuiZFHgwspEQwFc.webp";

// Related articles images
const ARTICLE1_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/hero-article1-v1-mBURY4muT8nNAjUnDwQRbz.webp";
const ARTICLE4_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/article4-market-hero_bed3bdbc.webp";
const ARTICLE3_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/article3-residential-complex-Vstb4C43kK3pRH4WYGacpF.webp";

const RELATED = [
  { tag: "Relocation", title: "Cost of Living in Brașov 2026: the honest numbers, neighbourhood by neighbourhood", img: ARTICLE4_IMG, href: "/cost-of-living-brasov" },
  { tag: "City Guide", title: "Brașov, straight up: beyond the bear photos and the mountain air clichés", img: ARTICLE1_IMG, href: "/why-expats-choose-brasov" },
  { tag: "Business", title: "Company Setup Romania 2026: Complete Guide for Expats", img: ARTICLE3_IMG, href: "/company-setup-romania" },
];

function NewsletterInlineLight() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle"|"loading"|"success"|"error">("idle");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setStatus("loading");
    try {
      await subscribeToNewsletter(email);
      setStatus("success");
    } catch { setStatus("error"); }
  };

  if (status === "success") return <p className="text-xs text-green-700 py-2">✓ Subscribed! See you Friday.</p>;

  return (
    <form onSubmit={handleSubmit}>
      <input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" className="w-full border border-stone-200 px-3 py-2 text-xs text-stone-700 outline-none focus:border-stone-400 mb-2" />
      <button type="submit" disabled={status === "loading"} className="w-full py-2 text-xs text-white font-semibold disabled:opacity-60" style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}>
        {status === "loading" ? "..." : "SUBSCRIBE"}
      </button>
      {status === "error" && <p className="text-xs text-red-500 mt-1">Try again.</p>}
    </form>
  );
}

function SidebarAd() {
  return (
    <div className="border border-stone-200 overflow-hidden">
      <div className="px-3 py-1.5" style={{ backgroundColor: "oklch(0.95 0.008 75)" }}>
        <span className="text-xs text-stone-400 uppercase tracking-widest">Partner Content · Cartier Transilvania</span>
      </div>
      <div className="relative">
        <img src="https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/XbbLvxSueOHmjHcF.webp" alt="Cartier Transilvania Brașov" className="w-full h-48 object-cover" loading="lazy" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, oklch(0.12 0.01 60 / 0.8) 0%, transparent 60%)" }} />
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <p className="text-white text-xs uppercase tracking-widest mb-1 font-semibold">New development · Noua</p>
          <h4 className="text-white font-bold leading-tight" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.1rem" }}>
            Cartier Transilvania
          </h4>
        </div>
      </div>
      <div className="p-4">
        <p className="text-stone-600 text-sm font-light leading-relaxed mb-3">
          208 apartments in 6 blocks. T1–T3 completed &amp; occupied. T4–T6 delivering September 2027. Next to Lake Noua.
        </p>
        <div className="flex items-center gap-1 text-xs text-stone-500 mb-4">
          <MapPin size={11} />
          <span>Noua, Brașov</span>
        </div>
        <a
          href="https://www.cartiertransilvania.ro/en/"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-1 text-xs text-white w-full px-3 py-2 transition-opacity hover:opacity-90"
          style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}>
          VIEW PROJECT <ExternalLink size={10} />
        </a>
      </div>
    </div>
  );
}

function InArticleBanner() {
  return (
    <div className="my-10 border border-stone-200 overflow-hidden">
      <div className="px-4 py-1.5" style={{ backgroundColor: "oklch(0.95 0.008 75)" }}>
        <span className="text-xs text-stone-400 uppercase tracking-widest">Partner content · Cartier Transilvania</span>
      </div>
      <div className="flex flex-col sm:flex-row">
        <img src="https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/XbbLvxSueOHmjHcF.webp" alt="Cartier Transilvania" className="w-full sm:w-48 h-36 object-cover flex-shrink-0" loading="lazy" />
        <div className="p-5 flex flex-col justify-between">
          <div>
            <p className="text-xs uppercase tracking-widest font-semibold mb-2" style={{ color: "oklch(0.38 0.12 15)" }}>
              Apartments for sale in Brașov
            </p>
            <h4 className="font-bold text-stone-900 mb-2 leading-snug" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.05rem" }}>
              Cartier Transilvania — 208 apartments in Noua
            </h4>
            <p className="text-stone-500 text-sm font-light leading-relaxed">
              T1, T2, T3 completed and occupied. T4, T5, T6 under construction — delivery September 2027. Solar panels &amp; heat pump heating.
            </p>
          </div>
          <div className="flex items-center gap-3 mt-4">
            <a
              href="https://www.cartiertransilvania.ro/en/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs text-white px-4 py-2 transition-opacity hover:opacity-90"
              style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}>
              VISIT PROJECT SITE <ChevronRight size={11} />
            </a>
            <span className="text-xs text-stone-400">cartiertransilvania.ro</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function FeaturedDeveloper() {
  return (
    <div className="mt-16 p-8 border-l-4" style={{ backgroundColor: "oklch(0.985 0.006 75)", borderLeftColor: "oklch(0.38 0.12 15)" }}>
      <div className="flex items-center gap-2 mb-5">
        <span className="text-xs text-stone-400 uppercase tracking-widest">Partner content · Cartier Transilvania</span>
      </div>
      <div className="flex flex-col md:flex-row gap-6 items-start">
        <img src="https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/XbbLvxSueOHmjHcF.webp" alt="Cartier Transilvania" className="w-full md:w-56 h-40 object-cover flex-shrink-0" loading="lazy" />
        <div>
          <p className="text-xs uppercase tracking-widest font-semibold mb-2" style={{ color: "oklch(0.38 0.12 15)" }}>
            Apartments for sale · Noua, Brașov
          </p>
          <h3 className="font-bold text-stone-900 mb-3 leading-snug" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.3rem" }}>
            Cartier Transilvania — a development built to last
          </h3>
          <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.7" }}>
            208 apartments across 6 blocks in the Noua neighbourhood. T1, T2, and T3 are completed and occupied. T4, T5, and T6 are under construction with a delivery date of September 2027. Solar panels and heat pump heating system included. Next to Lake Noua, Brașov Zoo, and Parc Aventura.
          </p>
          <a
            href="https://www.cartiertransilvania.ro/en/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-xs text-white px-5 py-2.5 transition-opacity hover:opacity-90"
            style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}>
            VISIT PROJECT SITE <ExternalLink size={11} />
          </a>
        </div>
      </div>
    </div>
  );
}

function ShareButton({ url, title }: { url: string; title: string }) {
  const [copied, setCopied] = useState(false);
  const handleShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title, url }); } catch { /* cancelled */ }
    } else {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  return (
    <button onClick={handleShare} className="flex items-center gap-2 text-xs text-stone-500 hover:text-stone-800 transition-colors">
      {copied ? <Check size={13} className="text-green-600" /> : <Share2 size={13} />}
      {copied ? "Copied!" : "Share"}
    </button>
  );
}

// Cost table — two columns (one-time / recurring)
function CostTable({ rows }: { rows: { item: string; type: string; amount: string }[] }) {
  return (
    <div className="my-6 w-full" style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
      <table
        className="border-collapse"
        style={{ width: "100%", tableLayout: "fixed", fontSize: "clamp(0.65rem, 2.2vw, 0.875rem)" }}
      >
        <colgroup>
          <col style={{ width: "50%" }} />
          <col style={{ width: "20%" }} />
          <col style={{ width: "30%" }} />
        </colgroup>
        <thead>
          <tr style={{ backgroundColor: "oklch(0.95 0.008 75)" }}>
            <th className="text-left text-stone-600 font-semibold uppercase tracking-wider border-b border-stone-200" style={{ padding: "clamp(0.4rem, 1.5vw, 0.75rem) clamp(0.5rem, 2vw, 1rem)", fontSize: "clamp(0.6rem, 1.8vw, 0.75rem)", letterSpacing: "0.05em" }}>Cost Item</th>
            <th className="text-right text-stone-600 font-semibold uppercase tracking-wider border-b border-stone-200" style={{ padding: "clamp(0.4rem, 1.5vw, 0.75rem) clamp(0.5rem, 2vw, 1rem)", fontSize: "clamp(0.6rem, 1.8vw, 0.75rem)", letterSpacing: "0.05em" }}>Type</th>
            <th className="text-right text-stone-600 font-semibold uppercase tracking-wider border-b border-stone-200" style={{ padding: "clamp(0.4rem, 1.5vw, 0.75rem) clamp(0.5rem, 2vw, 1rem)", fontSize: "clamp(0.6rem, 1.8vw, 0.75rem)", letterSpacing: "0.05em" }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-stone-100 hover:bg-stone-50/50 transition-colors">
              <td className="text-stone-800 font-medium" style={{ padding: "clamp(0.4rem, 1.5vw, 0.75rem) clamp(0.5rem, 2vw, 1rem)", wordBreak: "break-word", lineHeight: 1.4 }}>{row.item}</td>
              <td className="text-right text-stone-500" style={{ padding: "clamp(0.4rem, 1.5vw, 0.75rem) clamp(0.5rem, 2vw, 1rem)", wordBreak: "break-word", lineHeight: 1.4 }}>{row.type}</td>
              <td className="text-right text-stone-800 font-medium" style={{ padding: "clamp(0.4rem, 1.5vw, 0.75rem) clamp(0.5rem, 2vw, 1rem)", wordBreak: "break-word", lineHeight: 1.4 }}>{row.amount}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ArticleBuyingApartment() {
  return (
    <>
    <Helmet>
      <title>How to Buy an Apartment in Brașov as a Foreigner (2026 Guide)</title>
      <meta name="description" content="How to buy an apartment in Brașov as a foreigner: legal requirements, NIF, notary fees, online reservations, and the new Nordis Law. Complete 2026 guide." />
      <meta property="og:title" content="How to Buy an Apartment in Brașov as a Foreigner (2026 Guide)" />
      <meta property="og:description" content="How to buy an apartment in Brașov as a foreigner: legal requirements, NIF, notary fees, online reservations, and the new Nordis Law. Complete 2026 guide." />
      <meta property="og:image" content="https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/SIuiZFHgwspEQwFc.webp" />
      <meta property="og:image:width" content="1400" />
      <meta property="og:image:height" content="788" />
      <meta property="og:image:alt" content="Buy apartment Brașov as a foreigner — new residential buildings in Noua" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:type" content="article" />
      <meta property="og:url" content="https://brasovgateway.com/buying-apartment-foreigner-brasov" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="How to Buy an Apartment in Brașov as a Foreigner (2026 Guide)" />
      <meta name="twitter:description" content="The complete 2026 guide for expats buying property in Brașov, Romania. Legal requirements, NIF, online reservations, notary fees, and the new Nordis Law." />
      <meta name="twitter:image" content="https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/SIuiZFHgwspEQwFc.webp" />
      <meta name="keywords" content="buy apartment Brasov foreigner, buying property Romania expat, NIF Romania, notary fees Romania, Nordis Law, antecontract, real estate Brasov" />
      <link rel="canonical" href="https://brasovgateway.com/buying-apartment-foreigner-brasov" />
      <script type="application/ld+json">{`
        {
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Article",
              "@id": "https://brasovgateway.com/buying-apartment-foreigner-brasov#article",
              "headline": "How to Buy an Apartment in Brașov as a Foreigner (2026 Guide)",
              "description": "The complete 2026 guide for expats buying property in Brașov, Romania. Legal requirements, NIF, online reservations, notary fees, and the new Nordis Law.",
              "image": "${HERO_IMG}",
              "datePublished": "2026-04-15",
              "dateModified": "2026-04-15",
              "author": {
                "@type": "Organization",
                "name": "Brașov Gateway",
                "url": "https://brasovgateway.com"
              },
              "publisher": {
                "@type": "Organization",
                "name": "Brașov Gateway",
                "url": "https://brasovgateway.com"
              },
              "url": "https://brasovgateway.com/buying-apartment-foreigner-brasov",
              "inLanguage": "en",
              "articleSection": "Real Estate"
            },
            {
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Home",
                  "item": "https://brasovgateway.com"
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": "Blog",
                  "item": "https://brasovgateway.com/blog"
                },
                {
                  "@type": "ListItem",
                  "position": 3,
                  "name": "Buying an Apartment as a Foreigner",
                  "item": "https://brasovgateway.com/buying-apartment-foreigner-brasov"
                }
              ]
            }
          ]
        }
      `}</script>
    </Helmet>

    <div className="min-h-screen" style={{ fontFamily: "Source Sans 3, sans-serif", backgroundColor: "oklch(0.985 0.006 75)" }}>
      <SiteHeader />

      {/* Hero image */}
      <div className="relative h-72 md:h-96 mt-16">
        <img src={HERO_IMG} alt="Buy apartment Brașov as a foreigner — new residential buildings in Noua" className="w-full h-full object-cover" fetchPriority="high" width="1400" height="788" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, transparent 40%, oklch(0.985 0.006 75) 100%)" }} />
      </div>

      <div className="container py-8">
        <div className="flex flex-col lg:flex-row gap-12">
          {/* Main content */}
          <main className="flex-1 min-w-0">
            <article className="max-w-none">
              <header className="mb-10">
                <div className="flex items-center gap-3 mb-6">
                  <span className="text-xs font-bold uppercase tracking-widest px-3 py-1" style={{ backgroundColor: "oklch(0.38 0.12 15)", color: "white" }}>
                    Real Estate
                  </span>
                  <span className="text-xs text-stone-500 uppercase tracking-widest">April 2026</span>
                </div>
                <h1 className="text-stone-900 leading-tight mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "clamp(2rem, 4vw, 3rem)", fontWeight: 800 }}>
                  How to buy an apartment in Brașov as a foreigner: the 2026 guide
                </h1>
                <div className="flex items-center justify-between py-4 border-t border-b border-stone-200">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden" style={{ backgroundColor: "oklch(0.95 0.008 75)" }}>
                      <img src="https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/XbbLvxSueOHmjHcF.webp" alt="Brașov Gateway" className="w-full h-full object-cover opacity-50" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-stone-900">Brașov Gateway Editorial</p>
                      <p className="text-xs text-stone-500">Legal &amp; Real Estate Team</p>
                    </div>
                  </div>
                  <ShareButton url="https://brasovgateway.com/buying-apartment-foreigner-brasov" title="How to Buy an Apartment in Brașov as a Foreigner" />
                </div>
              </header>

              <p className="text-stone-600 text-lg md:text-xl font-light leading-relaxed mt-6 mb-2" style={{ lineHeight: "1.6" }}>
                From getting your Romanian tax number (NIF) to navigating the new Nordis Law protections for off-plan purchases. Here is exactly how the property buying process works for expats in Brașov.
              </p>

              {/* Quick Facts */}
              <div className="my-10 p-6 border-l-4" style={{ backgroundColor: "oklch(0.97 0.005 75)", borderLeftColor: "oklch(0.38 0.12 15)" }}>
                <h3 className="text-xs uppercase tracking-widest font-bold mb-4" style={{ color: "oklch(0.38 0.12 15)" }}>Quick Facts: Buying Property in Romania</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { label: "EU Citizens", value: "Full Rights", note: "Can buy apartments and land directly" },
                    { label: "Non-EU Citizens", value: "Apartments Only", note: "Cannot own land directly (need an SRL)" },
                    { label: "Required ID", value: "Passport + NIF", note: "NIF is the Romanian Tax Number" },
                    { label: "Notary Fees", value: "~0.5% - 1%", note: "Calculated based on property value" },
                    { label: "New Build VAT", value: "21%", note: "Standard rate in Romania, usually included in price" },
                    { label: "Remote Purchase", value: "Possible", note: "Via notarized Power of Attorney" },
                  ].map((fact, i) => (
                    <div key={i} className="p-4 border border-stone-100" style={{ backgroundColor: "oklch(0.985 0.006 75)" }}>
                      <p className="text-xs uppercase tracking-widest text-stone-400 mb-1">{fact.label}</p>
                      <p className="font-bold text-stone-900 text-sm leading-tight mb-1" style={{ fontFamily: "Playfair Display, serif" }}>{fact.value}</p>
                      <p className="text-xs text-stone-500 font-light">{fact.note}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Table of Contents */}
              <div className="my-8 p-6 border border-stone-200" style={{ backgroundColor: "oklch(0.97 0.005 75)" }}>
                <p className="text-xs uppercase tracking-widest font-semibold mb-4" style={{ color: "oklch(0.38 0.12 15)" }}>Table of Contents</p>
                <ol className="space-y-2">
                  {[
                    { n: "01", label: "The legal framework: EU vs. non-EU buyers", anchor: "#legal-framework" },
                    { n: "02", label: "Essential documents: the NIF and power of attorney", anchor: "#documents" },
                    { n: "03", label: "Buying new builds: reservation vs. antecontract", anchor: "#new-builds" },
                    { n: "04", label: "The Nordis Law (2025): new protections for buyers", anchor: "#nordis-law" },
                    { n: "05", label: "Taxes, notary fees, and total closing costs", anchor: "#costs" },
                  ].map((item) => (
                    <li key={item.n} className="flex items-start gap-3">
                      <span className="text-xs font-bold flex-shrink-0 mt-0.5" style={{ color: "oklch(0.38 0.12 15)" }}>{item.n}</span>
                      <a href={item.anchor} className="text-stone-600 text-sm font-light hover:text-stone-900 transition-colors no-underline" style={{ lineHeight: "1.5" }}>
                        {item.label}
                      </a>
                    </li>
                  ))}
                </ol>
              </div>

              {/* Section 1 */}
              <h2 id="legal-framework" className="text-stone-900 mt-12 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                The legal framework: EU vs. non-EU buyers
              </h2>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                Romania is generally welcoming to foreign investment, but the Constitution makes a strict distinction between owning a building and owning the land underneath it. This distinction dictates your entire buying strategy.
              </p>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                <strong className="text-stone-800">For EU and EEA Citizens:</strong> You have the exact same rights as Romanian citizens. You can buy apartments, houses, and land directly in your own name without any special restrictions.
              </p>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                <strong className="text-stone-800">For Non-EU Citizens (US, UK, Canada, Israel, etc.):</strong> You can legally buy and own an apartment (the physical structure) directly in your own name. Land ownership, however, is restricted by law: you cannot hold Romanian land directly as a non-EU individual. In practice, this is not a problem when buying an apartment — the land share beneath the building is automatically transferred to your company or registered as a superficie (right of use) in your name, which is the standard legal mechanism used across Romania. If you want to buy a house with a plot of land, you will need to <Link href="/company-setup-romania" className="text-stone-900 border-b border-dotted border-stone-400 hover:border-stone-900 transition-colors">open a Romanian SRL (Limited Liability Company)</Link> and purchase the property through the company.
              </p>

              {/* Section 2 */}
              <h2 id="documents" className="text-stone-900 mt-12 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                Essential documents: the NIF and power of attorney
              </h2>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                You do not need a Romanian residence permit or a specific visa to buy property. You can legally sign a purchase agreement even if you are in the country on a short-term tourist visa. However, you cannot simply show up at the notary with just your passport.
              </p>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                Every foreign buyer who does not have a Romanian CNP (Personal Numeric Code) must obtain a <strong>NIF (Număr de Identificare Fiscală)</strong> from ANAF (the Romanian tax authority). The notary needs this tax number to register the transaction and calculate taxes. You can obtain the NIF yourself by submitting a form and your passport copy to ANAF, or a lawyer can do it for you.
              </p>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                If you are buying remotely, you will need a <strong>Procură Notarială (Power of Attorney)</strong>. This document must be drafted by a Romanian lawyer or notary, translated, signed by you in front of a notary in your home country, and then Apostilled (if your country is part of the Hague Convention). This allows your representative in Brașov to sign the final deeds on your behalf.
              </p>

              {/* Section 3 */}
              <h2 id="new-builds" className="text-stone-900 mt-12 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                Buying new builds: reservation vs. antecontract
              </h2>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                Brașov's real estate market is booming with new developments, especially in areas like Tractorul and Noua. When buying off-plan or directly from a developer, the process usually involves two distinct preliminary steps before the final sale.
              </p>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                <strong>1. The Reservation (Rezervare):</strong> This is a simple commercial agreement, often signed online or via email. You pay a fee of up to 5% of the purchase price to take the apartment off the market for a period of up to 60 days. This gives you time to review the legal documents or secure financing. It does not transfer any property rights.
              </p>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                <strong>2. The Antecontract (Promisiune de vânzare-cumpărare):</strong> This is the serious legal commitment. It is signed in front of a Public Notary. You agree to buy, the developer agrees to sell at a fixed price, and you pay the down payment (avans). The antecontract outlines the delivery date, penalties for delays, and the exact specifications of the apartment.
              </p>

              {/* Section 4 */}
              <h2 id="nordis-law" className="text-stone-900 mt-12 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                The Nordis Law (2025): new protections for buyers
              </h2>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                Following a major real estate scandal, Romania implemented Law 207/2025 (commonly known as the "Nordis Law") to protect buyers of off-plan apartments. If you are buying a new build in Brașov in 2026, these protections apply to you:
              </p>
              <ul className="list-disc pl-5 mb-6 text-stone-600 font-light leading-relaxed space-y-2" style={{ lineHeight: "1.8" }}>
                <li><strong>Dedicated Bank Accounts:</strong> Developers must now deposit your down payment into a segregated project account. The funds can only be used for the construction of that specific building, preventing developers from using your money to fund other projects.</li>
                <li><strong>Reservation Limits:</strong> Reservation agreements are now strictly limited to a maximum of 60 days, and the reservation fee cannot exceed 5% of the total purchase price.</li>
                <li><strong>Mandatory Land Book Registration:</strong> The notary is now legally required to register your Antecontract in the Land Book (Carte Funciară) immediately. This ensures the developer cannot promise the same apartment to multiple buyers.</li>
              </ul>

              {/* Section 5 */}
              <h2 id="costs" className="text-stone-900 mt-12 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                Taxes, notary fees, and total closing costs
              </h2>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                When budgeting for an apartment in Brașov, you need to account for closing costs, which typically add <strong>3% to 5%</strong> to the purchase price. As in other European countries, the buyer pays the notary fees in Romania. If you are looking to reduce the total acquisition cost, it is worth knowing that Romania also has a government auction platform where seized and tax-enforcement properties are listed — sometimes at significant discounts. Our <Link href="/buy-property-romania-auctions-seized-assets" className="underline decoration-dotted underline-offset-2 hover:text-stone-900 transition-colors" style={{ color: "oklch(0.38 0.12 15)" }}>guide to buying property below market value in Romania</Link> explains how that route works and what the risks are.
              </p>

              <CostTable rows={[
                { item: "Notary Fees (Onorariu Notar)", type: "Distributed", amount: "~0.5% to 1% of property value, distributed across payment instalments" },
                { item: "Land Registry Fee (Taxa ANCPI)", type: "One-time", amount: "~50 RON (individuals); significantly higher for legal entities" },
                { item: "Real Estate Agent Commission", type: "One-time", amount: "0% (if buying direct from developer) or 2-3% (if using an agency)" },
                { item: "Value Added Tax (TVA)", type: "One-time", amount: "21% (standard rate in Romania). Usually included in the developer's advertised price." },
                { item: "Annual Property Tax", type: "Recurring", amount: "Calculated based on usable area and cadastral zone. Check the current rate with Brașov City Hall." },
              ]} />

              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                <strong>A note on the Land Book (Carte Funciară):</strong> This is the only proof of ownership that matters in Romania. Never pay a deposit or sign an agreement without your lawyer or notary pulling a fresh Land Book extract to verify that the seller actually owns the property and that there are no hidden mortgages or liens attached to it.
              </p>

              <FeaturedDeveloper />

            </article>
          </main>

          {/* Sidebar */}
          <aside className="w-full lg:w-72 flex-shrink-0">
            <div className="sticky top-24 flex flex-col gap-6">
              <SidebarAd />
              <div className="p-5 border border-stone-200 bg-white">
                <h4 className="font-bold text-stone-900 mb-3" style={{ fontFamily: "Playfair Display, serif" }}>
                  About Brașov Gateway
                </h4>
                <p className="text-stone-500 text-sm font-light leading-relaxed mb-4">
                  We help foreigners move to, invest in, and build a life in Brașov. Real estate, company setup, relocation support — all in one place.
                </p>
                <a href="/#contact"
                  className="block text-center text-xs text-white py-2.5 transition-opacity hover:opacity-90 no-underline"
                  style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}>
                  GET IN TOUCH
                </a>
              </div>
              <div className="mt-6 p-5" style={{ backgroundColor: "oklch(0.18 0.01 60)" }}>
                <h4 className="font-bold text-white mb-2" style={{ fontFamily: "Playfair Display, serif" }}>
                  Stay in the loop
                </h4>
                <p className="text-stone-300 text-xs font-light leading-relaxed mb-4">
                  No spam. One email per week with what is actually happening in Brașov.
                </p>
                <NewsletterInlineLight />
              </div>
              {/* CTA card — Real Estate */}
              <div className="p-5 text-white" style={{ backgroundColor: "oklch(0.38 0.12 15)" }}>
                <p className="text-xs uppercase tracking-widest mb-3 opacity-70">Real estate services</p>
                <h4 className="font-bold mb-3 leading-snug" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.1rem" }}>
                  Looking for an apartment in Brașov?
                </h4>
                <p className="text-sm font-light leading-relaxed mb-4 opacity-80">
                  We help foreigners find, evaluate, and buy property in Brașov — without the guesswork.
                </p>
                <Link href="/services/real-estate">
                  <button className="w-full text-xs py-2.5 border border-white/40 hover:bg-white/10 transition-colors tracking-widest">
                    VIEW SERVICE
                  </button>
                </Link>
              </div>
            </div>
          </aside>
        </div>
        {/* Related articles */}
        <div className="mt-20 pt-12 border-t border-stone-200">
          <div className="flex items-center gap-3 mb-8">
            <div className="h-px w-8" style={{ backgroundColor: "oklch(0.38 0.12 15)" }} />
            <span className="text-xs uppercase tracking-widest text-stone-400">More from the blog</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {RELATED.map((post, i) => (
              <Link key={i} href={post.href} className="group cursor-pointer no-underline block">
                <div className="overflow-hidden mb-4">
                  <img src={post.img} alt={post.title} className="w-full h-44 object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                </div>
                <span className="text-xs uppercase tracking-widest font-semibold" style={{ color: "oklch(0.38 0.12 15)" }}>{post.tag}</span>
                <h3 className="mt-2 text-base font-semibold text-stone-900 group-hover:text-stone-600 transition-colors leading-snug" style={{ fontFamily: "Playfair Display, serif" }}>
                  {post.title}
                </h3>
                <div className="mt-3 flex items-center gap-1 text-xs font-semibold" style={{ color: "oklch(0.38 0.12 15)", letterSpacing: "0.08em" }}>
                  READ MORE <ChevronRight size={11} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
      <SiteFooter />
    </div>
    </>
  );
}
